import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import {
  Button,
  Calendar,
  CalendarProps,
  Modal,
  Skeleton,
  Typography,
} from '@robinpowered/ui-kit';
import ChevronLeftOutline from '@robinpowered/ui-kit-icons/ChevronLeftOutline';
import ChevronRightOutline from '@robinpowered/ui-kit-icons/ChevronRightOutline';
import momentGenerateConfig from 'rc-picker/es/generate/moment';
import { useTranslation } from 'react-i18next';
import {
  Availability,
  useGetProfileEditSeriesAvailabilitiesQuery,
  useGetProfileEditSeriesInstancesQuery,
} from 'generated/graphql';
import type { ProfileReservation } from '../../utils/getReservationActions';
import { SHELL_OVERLAY_Z_INDEX } from 'constants/zIndex';

const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Maximum months into the future the calendar allows, aligned with the
 * office app's booking window (backend instance expansion limit).
 */
const MAX_FUTURE_MONTHS = 12;

type DayStatus = 'assigned-to-me' | 'available' | 'unavailable';

type EditDeskSeriesModalProps = {
  open: boolean;
  reservation: ProfileReservation | null;
  /** True while the RDATE update (and its refetches) are in flight. */
  saving: boolean;
  afterOpenChange?: (open: boolean) => void;
  onClose: () => void;
  /** Receives the full RDATE rule for the series' new set of dates. */
  onSave: (rdateRule: string) => void;
};

const MomentCalendar =
  Calendar.generateCalendar<moment.Moment>(momentGenerateConfig);

// Mirrors the office app's month normalization: anchor mid-month at noon so
// month arithmetic never lands on a DST boundary or spills into a neighbor.
const toMonthAnchor = (date: moment.Moment): moment.Moment =>
  date
    .clone()
    .startOf('month')
    .add(15, 'days')
    .hour(12)
    .minute(0)
    .second(0)
    .millisecond(0);

export const EditDeskSeriesModal = ({
  open,
  reservation,
  saving,
  afterOpenChange,
  onClose,
  onSave,
}: EditDeskSeriesModalProps): JSX.Element | null => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'bookings.editSeriesModal',
  });

  const timezone =
    reservation?.timeZone ?? reservation?.seat.location.timezone ?? 'UTC';
  const seriesId = reservation?.seriesId ?? null;
  const deskId = reservation?.seat.id ?? null;
  const reserveeUserId = Number(reservation?.reservee?.user?.id);

  const [visibleMonth, setVisibleMonth] = useState<moment.Moment>(() =>
    toMonthAnchor(moment.tz(timezone))
  );
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  // Re-seed the calendar from the series each time the modal opens.
  useEffect(() => {
    if (open) {
      setInitialized(false);
    }
  }, [open]);

  const { data: instancesData, loading: instancesLoading } =
    useGetProfileEditSeriesInstancesQuery({
      skip: !open || !seriesId,
      fetchPolicy: 'network-only',
      variables: {
        seriesId: seriesId ?? '',
      },
    });
  const instances = instancesData?.getDeskReservationInstancesById;

  // Every date the series currently occupies, as calendar day keys in the
  // desk's timezone.
  const seriesDates = useMemo(() => {
    const dates = new Set<string>();
    instances?.forEach((instance) => {
      dates.add(moment.tz(instance.startTime, timezone).format(DATE_FORMAT));
    });
    return dates;
  }, [instances, timezone]);

  // The series' time-of-day, from its first instance. All instances of a
  // series share the same local start time.
  const seriesStart = useMemo(
    () =>
      moment.tz(
        instances?.[0]?.startTime ?? reservation?.startTime ?? undefined,
        timezone
      ),
    [instances, reservation?.startTime, timezone]
  );

  const durationInMinutes = useMemo(() => {
    if (!reservation?.endTime) {
      return null;
    }
    return moment(reservation.endTime).diff(
      moment(reservation.startTime),
      'minutes'
    );
  }, [reservation?.startTime, reservation?.endTime]);

  // Availability is fetched one visible month at a time, from today forward.
  const queryDates = useMemo(() => {
    const now = moment.tz(timezone);
    const start = visibleMonth.isSame(now, 'month')
      ? now.clone().startOf('day')
      : visibleMonth.clone().startOf('month');
    const end = visibleMonth.clone().endOf('month');

    const dates: string[] = [];
    for (
      const day = start.clone();
      day.isSameOrBefore(end);
      day.add(1, 'day')
    ) {
      dates.push(day.format(DATE_FORMAT));
    }
    return dates;
  }, [visibleMonth, timezone]);

  const { data: availabilityData, loading: availabilityLoading } =
    useGetProfileEditSeriesAvailabilitiesQuery({
      skip:
        !open ||
        !deskId ||
        !queryDates.length ||
        !Number.isInteger(reserveeUserId) ||
        durationInMinutes === null,
      fetchPolicy: 'network-only',
      variables: {
        deskId: deskId ?? '',
        dates: queryDates,
        startTime: seriesStart.format('HH:mm'),
        durationInMinutes: durationInMinutes ?? 0,
        timezone,
        userId: reserveeUserId,
        excludedReservationIds: instances?.map((instance) => instance.id) ?? [],
      },
    });

  const availabilityByDate = useMemo(() => {
    const map = new Map<string, Availability>();
    availabilityData?.getReservationsMultiDayAvailabilities?.forEach(
      (dayAvailability) => {
        map.set(
          moment.tz(String(dayAvailability.date), timezone).format(DATE_FORMAT),
          dayAvailability.availability
        );
      }
    );
    return map;
  }, [availabilityData, timezone]);

  // Seed the selection with the series' future instances once they load.
  useEffect(() => {
    if (!open || initialized || !instances) {
      return;
    }

    const now = moment.tz(timezone);
    const initialDates = new Set<string>();
    instances.forEach((instance) => {
      if (
        instance.endTime &&
        moment.tz(instance.endTime, timezone).isSameOrAfter(now)
      ) {
        initialDates.add(
          moment.tz(instance.startTime, timezone).format(DATE_FORMAT)
        );
      }
    });

    setSelectedDates(initialDates);
    setVisibleMonth(toMonthAnchor(seriesStart));
    setInitialized(true);
  }, [open, initialized, instances, seriesStart, timezone]);

  const maxDate = useMemo(
    () => moment.tz(timezone).add(MAX_FUTURE_MONTHS, 'months').endOf('day'),
    [timezone]
  );

  const getDayStatus = useCallback(
    (date: moment.Moment): DayStatus => {
      const dateStr = date.format(DATE_FORMAT);
      const isSeriesDate = seriesDates.has(dateStr);
      const isSelected = selectedDates.has(dateStr);

      // Past instances: once a day's start time has passed it's immutable —
      // series instances stay visible as booked, the rest are out of reach.
      // Time precision (not day) so today's already-started instance still
      // counts as past, matching the office app.
      const instanceStart = moment
        .tz(`${dateStr}T00:00:00`, timezone)
        .hour(seriesStart.hour())
        .minute(seriesStart.minute());
      if (instanceStart.isBefore(moment.tz(timezone))) {
        return isSeriesDate ? 'assigned-to-me' : 'unavailable';
      }

      const availability = availabilityByDate.get(dateStr);
      if (!availability) {
        return isSelected ? 'assigned-to-me' : 'available';
      }

      switch (availability) {
        case Availability.Available:
          return isSelected ? 'assigned-to-me' : 'available';
        case Availability.Booked:
          return isSelected ? 'assigned-to-me' : 'unavailable';
        default:
          return 'unavailable';
      }
    },
    [availabilityByDate, selectedDates, seriesDates, seriesStart, timezone]
  );

  const toggleDate = useCallback((date: moment.Moment) => {
    const dateStr = date.format(DATE_FORMAT);
    setSelectedDates((current) => {
      const next = new Set(current);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else {
        next.add(dateStr);
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(() => {
    // The new rule keeps every past instance (immutable) and replaces the
    // future ones with the current selection. All at the series' own local
    // start time, converted to UTC — built entirely in the desk's timezone
    // so the RDATE never drifts a day for viewers in other timezones.
    const now = moment.tz(timezone);
    const bookedDates = new Set(selectedDates);
    seriesDates.forEach((dateStr) => {
      // Time precision, matching getDayStatus and the office app: today's
      // already-started instance is immutable and must survive the rewrite.
      const instanceStart = moment
        .tz(`${dateStr}T00:00:00`, timezone)
        .hour(seriesStart.hour())
        .minute(seriesStart.minute());
      if (instanceStart.isBefore(now)) {
        bookedDates.add(dateStr);
      }
    });

    const rdates = Array.from(bookedDates)
      .sort()
      .map((dateStr) =>
        moment
          .tz(`${dateStr}T00:00:00`, timezone)
          .hour(seriesStart.hour())
          .minute(seriesStart.minute())
          .utc()
          .format('YYYYMMDD[T]HHmmss[Z]')
      );

    onSave(`RDATE:${rdates.join(',')}`);
  }, [onSave, selectedDates, seriesDates, seriesStart, timezone]);

  const cellRender: CalendarProps<moment.Moment>['cellRender'] = (
    current,
    info
  ) => {
    if (info.type !== 'date') {
      return null;
    }

    if (!current.isSame(visibleMonth, 'month')) {
      return <HiddenDateCell />;
    }

    const isPastDay = current.isBefore(moment.tz(timezone), 'day');
    const isBeyondMax = current.isAfter(maxDate, 'day');
    const dayStatus: DayStatus = isBeyondMax
      ? 'unavailable'
      : getDayStatus(current);
    const isSelectable =
      !isPastDay && !isBeyondMax && dayStatus !== 'unavailable';

    const CellContentComponent =
      dayStatus === 'assigned-to-me'
        ? CellContentAssigned
        : dayStatus === 'unavailable'
        ? CellContentUnavailable
        : CellContentAvailable;

    return (
      <CellWrapper>
        <CellContentComponent>
          <CellButton
            data-testid={`calendar-cell-${current.date()}-${dayStatus}`}
            $status={dayStatus}
            $isSelectable={isSelectable}
            type="button"
            disabled={!isSelectable}
            onClick={() => toggleDate(current)}
          >
            {current.date()}
          </CellButton>
        </CellContentComponent>
      </CellWrapper>
    );
  };

  const headerRender = ({
    value,
    onChange,
  }: {
    value: moment.Moment;
    onChange: (date: moment.Moment) => void;
  }) => {
    const isAtMaxMonth = value
      .clone()
      .add(1, 'months')
      .isAfter(maxDate, 'month');

    const changeMonth = (offset: number) => {
      const next = toMonthAnchor(value.clone().add(offset, 'months'));
      onChange(next);
      setVisibleMonth(next);
    };

    return (
      <CalendarHeader>
        <HeaderIconButton
          type="button"
          aria-label={t('previousMonth')}
          onClick={() => changeMonth(-1)}
        >
          <ChevronLeftOutline />
        </HeaderIconButton>
        <HeaderTitle data-testid="calendar-month-year">
          {value.format('MMMM YYYY')}
        </HeaderTitle>
        <HeaderIconButton
          type="button"
          aria-label={t('nextMonth')}
          disabled={isAtMaxMonth}
          onClick={() => changeMonth(1)}
        >
          <ChevronRightOutline />
        </HeaderIconButton>
      </CalendarHeader>
    );
  };

  if (!reservation) {
    return null;
  }

  const loading = instancesLoading || availabilityLoading || !initialized;

  return (
    <Modal
      open={open}
      zIndex={SHELL_OVERLAY_Z_INDEX}
      title={t('title')}
      afterOpenChange={afterOpenChange}
      onCancel={onClose}
      width={575}
      modalRender={(modalNode) => (
        <ModalEventBoundary
          onClick={stopModalEvent}
          onMouseDown={stopModalEvent}
          onPointerDown={stopModalEvent}
        >
          {modalNode}
        </ModalEventBoundary>
      )}
      footer={
        <>
          <Button type="default" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={loading || selectedDates.size === 0}
          >
            {t('save')}
          </Button>
        </>
      }
    >
      <div>
        <Typography.Text>{t('description')}</Typography.Text>
      </div>
      {loading ? (
        <CalendarLoadingContainer data-testid="skeleton">
          <Skeleton />
          <Skeleton />
        </CalendarLoadingContainer>
      ) : (
        <CalendarContainer>
          <MomentCalendar
            fullscreen={false}
            fullCellRender={cellRender}
            value={visibleMonth}
            onPanelChange={(value) => setVisibleMonth(toMonthAnchor(value))}
            headerRender={headerRender}
          />
        </CalendarContainer>
      )}
      <Legend data-testid="calendar-legend">
        <LegendItem>
          <LegendSwatch $status="assigned-to-me" />
          <Typography.Text>{t('assignedToMe')}</Typography.Text>
        </LegendItem>
        <LegendItem>
          <LegendSwatch $status="available" />
          <Typography.Text>{t('available')}</Typography.Text>
        </LegendItem>
        <LegendItem>
          <LegendSwatch $status="unavailable" />
          <Typography.Text>{t('unavailable')}</Typography.Text>
        </LegendItem>
      </Legend>
    </Modal>
  );
};

const stopModalEvent = (event: React.SyntheticEvent<HTMLDivElement>) => {
  event.stopPropagation();
  event.nativeEvent.stopImmediatePropagation?.();
};

const CellWrapper = styled.div`
  display: flex;
  padding: 2px 6px;
  justify-content: center;
  align-items: flex-start;
`;

const HiddenDateCell = styled.div`
  visibility: hidden;
`;

const ModalEventBoundary = styled.div``;

const CalendarHeader = styled.div`
  display: flex;
  padding: 8px;
  align-items: center;
  justify-content: space-between;
`;

const HeaderIconButton = styled.button`
  appearance: none;
  display: flex;
  width: 24px;
  height: 24px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: inherit;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

const HeaderTitle = styled.span`
  font-size: 16px;
`;

const sharedCellContentStyles = `
  display: flex;
  width: 28px;
  height: 28px;
  min-width: 28px;
  padding: 0px 3px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: var(--ant-border-radius, 4px);
  transition: all 0.2s ease;
`;

const CellContentAssigned = styled.div`
  ${sharedCellContentStyles}
  background: var(--ant-color-info, #2774c1);
`;

const CellContentAvailable = styled.div`
  ${sharedCellContentStyles}
  border: var(--ant-line-width, 1px) solid var(--ant-color-info, #2774c1);
`;

const CellContentUnavailable = styled.div`
  ${sharedCellContentStyles}
  background: var(--ant-color-bg-container-disabled, #f0f0f0);
`;

const CellButton = styled.button<{
  $status: DayStatus;
  $isSelectable: boolean;
}>`
  appearance: none;
  width: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ $status }) =>
    $status === 'assigned-to-me'
      ? 'var(--ant-color-text-light-solid, #fff)'
      : 'var(--ant-color-text, #1c1c1c)'};
  font-weight: ${({ $status }) =>
    $status === 'assigned-to-me' ? 'bold' : 'normal'};
  text-align: center;
  line-height: 22px;
  cursor: ${({ $isSelectable }) => ($isSelectable ? 'pointer' : 'default')};

  &:disabled {
    cursor: not-allowed;
  }
`;

const Legend = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--ant-margin-xs, 8px);
  align-self: stretch;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: var(--ant-margin-xs, 8px);
`;

const LegendSwatch = styled.span<{ $status: DayStatus }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 100px;
  background: ${({ $status }) =>
    $status === 'assigned-to-me'
      ? 'var(--ant-color-info, #2774c1)'
      : 'var(--ant-color-bg-container, #fff)'};
  border: 1px solid
    ${({ $status }) =>
      $status === 'assigned-to-me'
        ? 'transparent'
        : $status === 'unavailable'
        ? 'var(--ant-color-border-secondary, #d6d6d6)'
        : 'var(--ant-color-info, #2774c1)'};
`;

const CalendarLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
`;

const CalendarContainer = styled.div`
  padding: 16px 0;
`;
